import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

const timeout = 30000;

// En développement, utiliser les URLs relatives qui passent par le proxy Vite
// En production, utiliser les URLs depuis les variables d'environnement
const isDev = import.meta.env.DEV;
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
const apiRunnerBaseUrl = import.meta.env.VITE_API_RUNNER_BASE_URL;
const statisticsBaseUrl = import.meta.env.VITE_STATISTICS_API_BASE_URL;

// En production, VITE_API_BASE_URL doit être défini avec une URL absolue
// Exemple: https://preprod.api.runnrz.app/api/admin/
if (!isDev && !apiBaseUrl) {
  console.warn('VITE_API_BASE_URL is not defined. API calls may fail.');
}

if (!isDev && !apiRunnerBaseUrl) {
  console.warn('VITE_API_RUNNER_BASE_URL is not defined. API calls may fail.');
}

if (!isDev && !statisticsBaseUrl) {
  console.warn('VITE_STATISTICS_API_BASE_URL is not defined. Statistics API calls may fail.');
}

export const API_BASE_URL = isDev ? '/api/admin/' : (apiBaseUrl || '/api/admin/');
export const API_RUNNER_BASE_URL = isDev ? '/api/runner/' : (apiRunnerBaseUrl || '/api/runner/');
export const STATISTICS_API_BASE_URL = isDev ? '/statistics/' : (statisticsBaseUrl || '/statistics/');

export default class Client {
  request(
    token: string | null,
    method: string,
    uri: string,
    data?: any,
    params?: any,
    responseType: string = ''
  ): Promise<AxiosResponse> {
    const path = uri.replace(API_BASE_URL, '');
    const url = `${API_BASE_URL}${path}`;
    const headers = this.headers(token);
    const config: AxiosRequestConfig = {
      headers,
      method: method as any,
      timeout,
      url,
      data,
      params,
      responseType: responseType as any,
      withCredentials: true, // Permet l'envoi automatique des cookies de session
    };
    return axios(config);
  }

  requestMultipart(
    token: string | null,
    method: string,
    uri: string,
    data?: any,
    params?: any,
    responseType: string = ''
  ): Promise<AxiosResponse> {
    const path = uri.replace(API_BASE_URL, '');
    const url = `${API_BASE_URL}${path}`;
    const headers = this.headersMultipart(token);
    const config: AxiosRequestConfig = {
      headers,
      method: method as any,
      timeout,
      url,
      data,
      params,
      responseType: responseType as any,
      withCredentials: true, // Permet l'envoi automatique des cookies de session
    };
    return axios(config);
  }

  requestStatistics(
    token: string | null,
    method: string,
    uri: string,
    data?: any,
    params?: any,
    responseType: string = ''
  ): Promise<AxiosResponse> {
    const path = uri.replace(STATISTICS_API_BASE_URL, '');
    const url = `${STATISTICS_API_BASE_URL}${path}`;
    const headers = this.headers(token);
    const config: AxiosRequestConfig = {
      headers,
      method: method as any,
      timeout,
      url,
      data,
      params,
      responseType: responseType as any,
      withCredentials: true, // Permet l'envoi automatique des cookies de session
    };
    return axios(config);
  }

  dl(
    token: string | null,
    method: string,
    uri: string,
    data?: any,
    params?: any,
    responseType: string = ''
  ): Promise<AxiosResponse> {
    const path = uri.replace(API_BASE_URL, '');
    const url = `${API_BASE_URL}${path}`;
    const headers = {
      Accept: 'application/pdf',
      Authorization: `Token ${token}`,
    };
    const config: AxiosRequestConfig = {
      headers,
      method: method as any,
      timeout,
      url,
      data,
      params,
      responseType: responseType as any,
      withCredentials: true, // Permet l'envoi automatique des cookies de session
    };
    return axios(config);
  }

  private headers(token: string | null): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    return token ? { ...headers, Authorization: `Token ${token}` } : headers;
  }

  private headersMultipart(token: string | null): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'multipart/form-data',
      Accept: 'application/json',
    };
    return token ? { ...headers, Authorization: `Token ${token}` } : headers;
  }

  requestRunner(
    token: string | null,
    method: string,
    uri: string,
    data?: any,
    params?: any,
    responseType: string = ''
  ): Promise<AxiosResponse> {
    const path = uri.replace(API_RUNNER_BASE_URL, '');
    const url = `${API_RUNNER_BASE_URL}${path}`;
    const headers = this.headers(token);
    const config: AxiosRequestConfig = {
      headers,
      method: method as any,
      timeout,
      url,
      data,
      params,
      responseType: responseType as any,
      withCredentials: true, // Permet l'envoi automatique des cookies de session
    };
    return axios(config);
  }
}

